import { log } from '@lib/logs';
import type { Redis, RedisOptions } from 'ioredis';
import type { NextApiRequest, NextApiResponse } from 'next';

// TTL for Redis keys storing metrics, defaulting to 7 days if not set or invalid
const REDIS_METRIC_TTL_SECONDS =
  Number.parseInt(process.env.FETCH_METRICS_TTL_SECONDS || '', 10) ||
  60 * 60 * 24 * 7;

// Sample rate for logging successful fetch requests, defaulting to 10% if not set or invalid
const FETCH_SUCCESS_SAMPLE_RATE = Number.isNaN(
  Number.parseFloat(process.env.FETCH_SUCCESS_SAMPLE_RATE || '')
)
  ? 0.1
  : Number.parseFloat(process.env.FETCH_SUCCESS_SAMPLE_RATE || '');

// Threshold for considering a fetch request as "slow", defaulting to 1500ms if not set or invalid
const FETCH_SLOW_REQUEST_MS =
  Number.parseInt(process.env.FETCH_SLOW_REQUEST_MS || '', 10) || 1500;

let redisClient: Redis | null = null;

// Fetch logging is enabled only in production when Redis connection details are provided
const isFetchLoggingEnabled = () => {
  return (
    // process.env.ENVIRONMENT === 'production' &&
    process.env.REDIS_HOST && process.env.REDIS_PORT
  );
};

// Clamp a number between a minimum and maximum value
const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

const safeSampleRate = clamp(FETCH_SUCCESS_SAMPLE_RATE, 0, 1);

const getRedisClient = (): Redis | null => {
  if (!process.env.REDIS_HOST || !process.env.REDIS_PORT) {
    return null;
  }

  if (redisClient && redisClient.status !== 'end') {
    return redisClient;
  }

  const option: RedisOptions = {
    host: process.env.REDIS_HOST,
    password: decodeURIComponent(process.env.REDIS_PASSWORD || ''),
    username: process.env.REDIS_USERNAME || '',
    port: Number.parseInt(process.env.REDIS_PORT, 10),
    connectTimeout: 2000,
  };

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { Redis: RedisClass } = require('ioredis') as typeof import('ioredis');
  redisClient = new RedisClass(option);
  redisClient.on('error', (error) => {
    log({ error, location: 'customFetch.redis' });
  });

  return redisClient;
};

// Classify the service based on the hostname for better aggregation in metrics
const classifyService = (hostname: string): string => {
  const host = hostname.toLowerCase();

  if (!host || host === 'internal') {
    return 'internal';
  }
  if (host.includes('bigcommerce')) {
    return 'bigcommerce';
  }
  if (host.includes('sanity')) {
    return 'sanity';
  }
  if (host.includes('cloudinary')) {
    return 'cloudinary';
  }

  return host;
};

// Normalize the path by removing query parameters, fragments, UUIDs, emails, and IDs
const normalizePath = (rawPath: string): string => {
  return rawPath
    .replace(/\?.*$/, '')
    .replace(/#.*$/, '')
    .replace(
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g,
      '<UUID>'
    )
    .replace(/\b[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}\b/g, '<EMAIL>')
    .replace(/\b\d{5,}\b/g, '<ID>')
    .replace(/\/+/g, '/');
};

// Extract the path and hostname from the input, which can be a string, URL, or Request object
const getPathAndHost = (
  input: string | URL | Request
): { normalizedPath: string; hostname: string } => {
  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  try {
    const parsed = new URL(rawUrl);
    return {
      normalizedPath: normalizePath(parsed.pathname || '/'),
      hostname: parsed.hostname || 'internal',
    };
  } catch {
    return {
      normalizedPath: normalizePath(rawUrl || '/'),
      hostname: 'internal',
    };
  }
};

const getDurationBucket = (durationMs: number): string => {
  if (durationMs < 100) {
    return '<100ms';
  }
  if (durationMs < 300) {
    return '100-299ms';
  }
  if (durationMs < 1000) {
    return '300-999ms';
  }
  if (durationMs < 3000) {
    return '1-3s';
  }
  return '3s+';
};

type LogDecision = {
  shouldLog: boolean;
  mode: 'sampled' | 'forced';
};

const getSampleRateLabel = (sampleRate: number): string => {
  return sampleRate.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

// Errors and slow requests are always logged. Normal traffic is sampled.
const getLogDecision = ({
  status,
  durationMs,
}: {
  status: number;
  durationMs: number;
}): LogDecision => {
  const isError = status === 0 || status >= 400;
  const isSlow = durationMs >= FETCH_SLOW_REQUEST_MS;

  if (isError || isSlow) {
    return { shouldLog: true, mode: 'forced' };
  }

  return {
    shouldLog: Math.random() < safeSampleRate,
    mode: 'sampled',
  };
};

const logRequest = async ({
  path,
  method,
  status,
  durationMs,
  hostname,
  source,
}: {
  path: string;
  method: string;
  status: number;
  durationMs: number;
  hostname: string;
  source: 'fetch' | 'handler';
}): Promise<void> => {
  if (!isFetchLoggingEnabled()) {
    return;
  }

  const logDecision = getLogDecision({ status, durationMs });
  if (!logDecision.shouldLog) {
    return;
  }

  const client = getRedisClient();
  if (!client) {
    return;
  }

  const minuteBucket = new Date().toISOString().slice(0, 16);
  const key = `metrics:requests:${minuteBucket}`;
  const safeMethod = (method || 'GET').toUpperCase();
  const statusClass = status === 0 ? 'error' : `${Math.floor(status / 100)}xx`;
  const service = classifyService(hostname);
  const durationBucket = getDurationBucket(durationMs);
  const normalizedPath = path || '/';
  const sampleRateLabel = getSampleRateLabel(safeSampleRate);
  const routeBaseKey = `${service}|${safeMethod}|${normalizedPath}`;
  const routeKey = `${service}|${safeMethod}|${normalizedPath}|${statusClass}`;

  const redisTransaction = client
    .multi()
    .hincrby(key, 'count:total', 1)
    .hincrby(key, `count:source:${source}`, 1)
    .hincrby(key, `count:mode:${logDecision.mode}`, 1)
    .hincrby(key, `count:service:${service}`, 1)
    .hincrby(key, `count:method:${safeMethod}`, 1)
    .hincrby(key, `count:status:${statusClass}`, 1)
    .hincrby(key, `count:${logDecision.mode}:status:${statusClass}`, 1)
    .hincrby(key, `count:status_code:${status}`, 1)
    .hincrby(key, `count:duration_bucket:${durationBucket}`, 1)
    .hincrby(
      key,
      `count:duration_bucket:service:${service}:${durationBucket}`,
      1
    )
    .hincrby(
      key,
      `count:duration_bucket:route:${routeBaseKey}:${durationBucket}`,
      1
    )
    .hincrby(key, `count:route:${routeKey}`, 1)
    .hincrby(key, `count:route_mode:${logDecision.mode}:${routeKey}`, 1);

  if (logDecision.mode === 'sampled') {
    redisTransaction.hincrby(key, `count:sample_rate:${sampleRateLabel}`, 1);
  }

  await redisTransaction.expire(key, REDIS_METRIC_TTL_SECONDS).exec();
};

/**
 * This function will log the request to Redis and then call the fetch function
 * It will log the path and method of the request along with the response status
 * @param input
 * @param init
 * @returns
 */
export async function customFetch(
  input: string | URL | Request,
  init?: RequestInit | undefined
): Promise<Response> {
  const startedAt = Date.now();
  const method =
    (init?.method || (input instanceof Request ? input.method : 'GET')) ??
    'GET';
  const { normalizedPath, hostname } = getPathAndHost(input);

  try {
    const response = await fetch(input, init);

    void logRequest({
      path: normalizedPath,
      method,
      status: response.status,
      durationMs: Date.now() - startedAt,
      hostname,
      source: 'fetch',
    }).catch((error) => {
      log({ error, location: 'customFetch.logRequest' });
    });

    return response;
  } catch (error) {
    void logRequest({
      path: normalizedPath,
      method,
      status: 0,
      durationMs: Date.now() - startedAt,
      hostname,
      source: 'fetch',
    }).catch((logError) => {
      log({ error: logError, location: 'customFetch.logRequest' });
    });

    throw error;
  }
}

// This defines a generic function wrapper.
// T extends a Next.js API handler signature, so fn must be a valid handler.
// Returning Promise<T> preserves the wrapped function's call signature.
export async function handlerWrapper<
  T extends (req: NextApiRequest, res: NextApiResponse) => unknown,
>(fn: T): Promise<T> {
  return (async (
    req: NextApiRequest,
    res: NextApiResponse
  ): Promise<ReturnType<T>> => {
    const startedAt = Date.now();

    try {
      return (await fn(req, res)) as ReturnType<T>;
    } finally {
      void logRequest({
        path: normalizePath(req.url || '/'),
        method: req.method || 'GET',
        status: res.statusCode || 200,
        durationMs: Date.now() - startedAt,
        hostname: 'internal',
        source: 'handler',
      }).catch((error) => {
        log({ error, location: 'handlerWrapper.logRequest' });
      });
    }
  }) as T; // Type assertion to ensure the returned function matches the original's type
}
